'use client';

import {
  deleteUncroppedUntriagedClipsApi,
  getChallengeTypeLabel,
  getMissingClipsApi,
  type IMissingClipChallenge,
} from '@repo/sdk';
import { Button, toast } from '@repo/ui';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { type JSX, useCallback, useEffect, useState } from 'react';
import type { IStepActionStatus } from '../use-step-action';

interface IMissingClipsPanelProps {
  collectionId: number;
  count: number;
  status: IStepActionStatus;
  log: string;
}

export function MissingClipsPanel({
  collectionId,
  count,
  status,
  log,
}: IMissingClipsPanelProps): JSX.Element {
  const router = useRouter();
  const [challenges, setChallenges] = useState<IMissingClipChallenge[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  useEffect(() => {
    if (count === 0) {
      setChallenges(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getMissingClipsApi(collectionId)
      .then((res) => {
        if (!cancelled) {
          setChallenges(res.data?.data ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setChallenges([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [collectionId, count]);

  const cleanup = useCallback(() => {
    if (
      !window.confirm(
        'Delete every uncropped, untriaged clip in this collection (cropStatus pending/failed/null, no local file, no R2 path)? Approved/cropped/published clips are not touched. This is the recovery action for the Fill-missing bug.',
      )
    ) {
      return;
    }
    setCleaning(true);
    toast.promise(
      deleteUncroppedUntriagedClipsApi(collectionId)
        .then((res) => `Deleted ${res.data?.deleted ?? 0} uncropped clip(s)`)
        .finally(() => {
          setCleaning(false);
          router.refresh();
        }),
      { loading: 'Cleaning up…', success: (r) => r, error: 'Cleanup failed' },
    );
  }, [collectionId, router]);

  return (
    <div className="space-y-2">
      {loading && <p className="text-xs text-muted-foreground">Loading challenges…</p>}
      {challenges && challenges.length > 0 && (
        <div className="max-h-40 overflow-auto rounded border bg-muted/40 p-2">
          <ul className="space-y-1">
            {challenges.map((ch) => (
              <li key={ch.id} className="flex items-center gap-2 text-xs">
                <Link
                  href={`/sys/challenges/${ch.id}`}
                  className="text-primary hover:underline truncate"
                >
                  {ch.title}
                </Link>
                <span className="text-muted-foreground">{getChallengeTypeLabel(ch.type)}</span>
                <span className="ml-auto text-muted-foreground">#{ch.id}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {status !== 'idle' && (
        <pre className="max-h-40 overflow-auto rounded bg-muted p-2 text-xs whitespace-pre-wrap">
          {log || (status === 'running' ? 'Starting…' : '')}
        </pre>
      )}
      <div className="border-t pt-2 flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          Recovery: delete uncropped, untriaged clips.
        </p>
        <Button variant="destructive" size="sm" onClick={cleanup} disabled={cleaning}>
          {cleaning ? 'Cleaning…' : 'Delete uncropped'}
        </Button>
      </div>
    </div>
  );
}
